<div class="widget-doc" data-testid="widget-doc-container">
<div class="widget-doc__layout">

  <!-- ==================== Sticky bar: platform, theme, live URL ====================
       A bar over the content on narrow screens, a sidebar next to it on wide ones. -->
  <aside class="widget-doc__bar" data-testid="widget-doc-bar">
    <div class="widget-doc__bar-row">
      <div class="widget-doc__brand">
        <img class="widget-doc__brand-logo" [src]="'/assets/icons/' + skin() + '/logo/ev_navigation_logo.png'" alt="EV Navigation" />
        <span class="widget-doc__brand-title">Widget</span>
      </div>

      <fieldset class="widget-doc__segmented widget-doc__platforms" aria-label="Target platform"
           data-testid="widget-doc-platform">
        @for (p of platforms; track p.id) {
          <button type="button" class="widget-doc__segmented-option"
                  [class.widget-doc__segmented-option--active]="platform() === p.id"
                  [attr.aria-pressed]="platform() === p.id" [attr.data-testid]="'widget-doc-platform-' + p.id"
                  (click)="setPlatform(p.id)">{{ p.label }}</button>
        }
      </fieldset>

      <fieldset class="widget-doc__segmented widget-doc__theme" aria-label="Page theme" data-testid="widget-doc-theme">
        <button type="button" class="widget-doc__segmented-option" [class.widget-doc__segmented-option--active]="skin() === 'light'"
                [attr.aria-pressed]="skin() === 'light'" aria-label="Light page theme" data-testid="widget-doc-theme-light"
                (click)="setSkin('light')">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2.5v2.5M12 19v2.5M2.5 12H5M19 12h2.5M5.3 5.3l1.8 1.8M16.9 16.9l1.8 1.8M5.3 18.7l1.8-1.8M16.9 7.1l1.8-1.8" />
          </svg>
        </button>
        <button type="button" class="widget-doc__segmented-option" [class.widget-doc__segmented-option--active]="skin() === 'dark'"
                [attr.aria-pressed]="skin() === 'dark'" aria-label="Dark page theme" data-testid="widget-doc-theme-dark"
                (click)="setSkin('dark')">
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5Z" />
          </svg>
        </button>
      </fieldset>
    </div>

    <div class="widget-doc__url">
      <div class="widget-doc__url-text" data-testid="widget-doc-url">
        <span class="widget-doc__url-base">{{ widgetBase() }}?</span>@for (p of urlParams(); track p.name; let first = $first) {@if (!first) {<span class="widget-doc__url-amp">&amp;</span>}<span class="widget-doc__url-param" [class.widget-doc__url-param--placeholder]="p.name === 'api_key' && !hasApiKey()"><span class="widget-doc__url-name">{{ p.name }}</span>=<span class="widget-doc__url-value">{{ p.value }}</span></span>}
      </div>
      <div class="widget-doc__url-actions">
        <button type="button" class="widget-doc__url-button widget-doc__url-button--primary" data-testid="widget-doc-url-copy"
                (click)="copy('url', url())">{{ copiedId() === 'url' ? 'Copied' : 'Copy' }}</button>
        <a class="widget-doc__url-button" [href]="url()" target="_blank" rel="noopener" data-testid="widget-doc-url-open">Open ↗</a>
        <button type="button" class="widget-doc__url-button" [class.widget-doc__url-button--on]="importOpen()"
                [attr.aria-expanded]="importOpen()" data-testid="widget-doc-url-import" (click)="toggleImport()">Paste URL</button>
        <button type="button" class="widget-doc__url-button" data-testid="widget-doc-url-reset"
                [disabled]="setCount() === 0" (click)="resetAll()">Reset</button>
      </div>
    </div>

    @if (importOpen()) {
      <form class="widget-doc__import" (submit)="$event.preventDefault(); importUrl(importInput.value)">
        <input #importInput type="text" class="widget-doc__input" placeholder="Paste a widget URL or its query string"
               aria-label="Widget URL to load" data-testid="widget-doc-import-input" />
        <button type="submit" class="widget-doc__url-button widget-doc__url-button--primary" data-testid="widget-doc-import-apply">Load</button>
        @if (importError()) {
          <span class="widget-doc__import-error">That does not look like a widget URL.</span>
        }
      </form>
    }

    <div class="widget-doc__bar-status">
      {{ setCount() === 0 ? 'No parameters set — ' : setCount() + (setCount() === 1 ? ' parameter set — ' : ' parameters set — ') }}
      @if (hasApiKey()) {
        edit them in the sections; open the URL in a browser to test the configuration before building it into your app.
      } @else {
        <a href="#api-key">add your API key</a> to open a working widget.
      }
    </div>

    <!-- Sidebar only: the section list moves here from under the title. -->
    <nav class="widget-doc__side-toc" aria-label="Contents">
      @for (section of sections; track section.id; let i = $index) {
        <a class="widget-doc__side-toc-link" [href]="'#' + section.id"
           [attr.data-testid]="'widget-doc-side-toc-' + section.id">{{ i + 1 }}. {{ section.title }}</a>
      }
    </nav>
  </aside>

  <main class="widget-doc__main">

  <header class="widget-doc__hero">
    <h1 class="widget-doc__title">Widget Documentation</h1>
    <p class="widget-doc__lead">
      The EV Navigation widget is a web page that your <strong>Android</strong>, <strong>iOS</strong> or
      <strong>Flutter</strong> app shows in a WebView. It runs the same map, range and route calculations as
      the EV Navigation site; the URL configures the car, the settings, the colors and the map. Every value on
      this page is editable — the widget URL in the bar always reflects the current configuration.
    </p>
    <p class="widget-doc__lead">
      The widget is served at the root of its own host, <code>https://widget.&lt;domain&gt;/?…</code>.
    </p>
  </header>

  <nav class="widget-doc__toc" aria-label="Contents">
    @for (section of sections; track section.id; let i = $index) {
      <a class="widget-doc__toc-link" [href]="'#' + section.id"
         [attr.data-testid]="'widget-doc-toc-' + section.id">{{ i + 1 }}. {{ section.title }}</a>
    }
  </nav>

  <!-- One editable parameter: name, allowed values, fallback, description and the control. -->
  <ng-template #field let-param>
    <div class="widget-doc__field" [class.widget-doc__field--set]="isSet(param.name)"
         [attr.data-testid]="'widget-doc-field-' + param.name">
      <div class="widget-doc__field-head">
        <code class="widget-doc__param">{{ param.name }}</code>
        <code class="widget-doc__field-values" [title]="param.values">{{ param.values }}</code>
        <span class="widget-doc__field-spacer"></span>
        @if (isSet(param.name)) {
          <button type="button" class="widget-doc__field-clear" [attr.aria-label]="'Remove ' + param.name + ' from the URL'"
                  [attr.data-testid]="'widget-doc-clear-' + param.name" (click)="clear(param.name)">
            In URL <span aria-hidden="true">&#x2715;</span>
          </button>
        } @else {
          <span class="widget-doc__field-fallback">not set · {{ param.fallback }}</span>
        }
      </div>
      <p class="widget-doc__field-desc">{{ param.description }}</p>

      <div class="widget-doc__field-control">
        @switch (param.control.kind) {
          @case ('toggle') {
            <fieldset class="widget-doc__segmented" [attr.aria-label]="param.name">
              <button type="button" class="widget-doc__segmented-option"
                      [class.widget-doc__segmented-option--active]="value(param.name) === '0'"
                      [attr.aria-pressed]="value(param.name) === '0'" [attr.data-testid]="'widget-doc-' + param.name + '-0'"
                      (click)="setValue(param.name, '0')">Off · 0</button>
              <button type="button" class="widget-doc__segmented-option"
                      [class.widget-doc__segmented-option--active]="value(param.name) === '1'"
                      [attr.aria-pressed]="value(param.name) === '1'" [attr.data-testid]="'widget-doc-' + param.name + '-1'"
                      (click)="setValue(param.name, '1')">On · 1</button>
            </fieldset>
          }
          @case ('choice') {
            @if (param.control.select) {
              <select class="widget-doc__input widget-doc__select" [attr.aria-label]="param.name"
                      [attr.data-testid]="'widget-doc-input-' + param.name" (change)="onSelect(param.name, $event)">
                <option value="" [selected]="!isSet(param.name)">Not set</option>
                @for (option of param.control.options; track option.value) {
                  <option [value]="option.value" [selected]="value(param.name) === option.value">{{ option.label }} ({{ option.value }})</option>
                }
              </select>
            } @else {
              <fieldset class="widget-doc__segmented" [attr.aria-label]="param.name">
                @for (option of param.control.options; track option.value) {
                  <button type="button" class="widget-doc__segmented-option"
                          [class.widget-doc__segmented-option--active]="value(param.name) === option.value"
                          [attr.aria-pressed]="value(param.name) === option.value"
                          [attr.data-testid]="'widget-doc-' + param.name + '-' + option.value"
                          (click)="setValue(param.name, option.value)">{{ option.label }}</button>
                }
              </fieldset>
            }
          }
          @case ('range') {
            <div class="widget-doc__range">
              <input type="range" class="widget-doc__range-input" [min]="param.control.min" [max]="param.control.max"
                     [step]="param.control.step" [value]="rangeValue(param)" [style.--fill-value]="rangePercent(param)"
                     [attr.aria-label]="param.name"
                     [attr.data-testid]="'widget-doc-input-' + param.name" (input)="onRange(param, $event)" />
              <span class="widget-doc__range-value">{{ rangeLabel(param) }}</span>
            </div>
          }
          @case ('number') {
            @if (param.name === 'carid' && vehicleGroups().length > 0) {
              <select class="widget-doc__input widget-doc__select widget-doc__select--full" aria-labelledby="widget-doc-vehicle-picker-label" data-testid="widget-doc-vehicle-picker"
                      (change)="onSelect('carid', $event)">
                <option id="widget-doc-vehicle-picker-label" value="" [selected]="!isSet('carid')">Pick a vehicle…</option>
                @for (group of vehicleGroups(); track group.brand) {
                  <optgroup [label]="group.brand">
                    @for (vehicle of group.vehicles; track vehicle.id) {
                      <option [value]="vehicle.id" [selected]="value('carid') === '' + vehicle.id">{{ vehicle.label }} ({{ vehicle.id }})</option>
                    }
                  </optgroup>
                }
              </select>
            }
            <input type="number" class="widget-doc__input widget-doc__number" [min]="param.control.min ?? null"
                   [max]="param.control.max ?? null" [step]="param.control.step ?? 'any'" [placeholder]="param.control.placeholder"
                   [value]="value(param.name)" [attr.aria-label]="param.name"
                   [attr.data-testid]="'widget-doc-input-' + param.name" (change)="onNumber(param, $event)" />
            @if (param.name === 'carid' && selectedVehicleName()) {
              <span class="widget-doc__field-hint">{{ selectedVehicleName() }}</span>
            }
          }
          @case ('text') {
            <input type="text" class="widget-doc__input widget-doc__text" autocomplete="off" spellcheck="false"
                   [placeholder]="param.control.placeholder" [value]="value(param.name)" [attr.aria-label]="param.name"
                   [attr.data-testid]="'widget-doc-input-' + param.name" (input)="onText(param.name, $event)" />
          }
          @case ('chargers') {
            <fieldset class="widget-doc__chips" aria-label="Charger types">
              @for (type of chargerTypes; track type.value) {
                <button type="button" class="widget-doc__chip" [class.widget-doc__chip--on]="isCharger(type.value)"
                        [attr.aria-pressed]="isCharger(type.value)" [attr.data-testid]="'widget-doc-charger-' + type.value"
                        (click)="toggleCharger(type.value)">{{ type.label }}</button>
              }
            </fieldset>
          }
          @case ('operators') {
            @if (operators().length > 0) {
              <select class="widget-doc__input widget-doc__select widget-doc__select--full" aria-labelledby="widget-doc-operator-picker-label"
                      data-testid="widget-doc-operator-picker" (change)="addOperator($event)">
                <option id="widget-doc-operator-picker-label" value="" selected>Add an operator…</option>
                @for (operator of availableOperators(); track operator.id) {
                  <option [value]="operator.id">{{ operator.name }} ({{ operator.id }})</option>
                }
              </select>
            }
            <input type="text" class="widget-doc__input widget-doc__text" autocomplete="off" spellcheck="false"
                   placeholder="Operator ids, e.g. 12,345" [value]="value(param.name)" [attr.aria-label]="param.name"
                   [attr.data-testid]="'widget-doc-input-' + param.name" (change)="onText(param.name, $event)" />
            @if (selectedOperators().length > 0) {
              <fieldset class="widget-doc__chips" aria-label="Preferred operators">
                @for (operator of selectedOperators(); track operator.id) {
                  <button type="button" class="widget-doc__chip widget-doc__chip--on"
                          [attr.aria-label]="'Remove ' + (operator.name ?? operator.id)"
                          [attr.data-testid]="'widget-doc-operator-' + operator.id"
                          (click)="removeOperator(operator.id)">{{ operator.name ?? 'Unknown operator' }} ({{ operator.id }}) <span aria-hidden="true">&#x2715;</span></button>
                }
              </fieldset>
            }
          }
        }
      </div>
    </div>
  </ng-template>

  <!-- ==================== Integration ==================== -->
  <section id="integration" class="widget-doc__section">
    <h2 class="widget-doc__section-title">1. Integration — {{ platformInfo().label }}</h2>

    <app-modal-option-panel>
      <ol class="widget-doc__steps">
        <li>Configure the widget on this page and test the URL in a browser (<strong>Open ↗</strong> next to
          the URL).</li>
        <li>Show the URL in a WebView with JavaScript and DOM storage enabled — the code below already contains
          the current URL.</li>
        <li>Push the device position (and optionally the battery level) through the
          <a href="#native-bridge">native bridge</a>: the widget never asks the WebView for geolocation.</li>
      </ol>
    </app-modal-option-panel>

    @for (snippet of setupCode(); track snippet.id) {
      <app-modal-option-panel>
        <span panel-title>{{ snippet.title }}</span>
        <div class="widget-doc__code">
          <button type="button" class="widget-doc__copy" [attr.data-testid]="'widget-doc-copy-' + snippet.id"
                  (click)="copy(snippet.id, snippet.code)">{{ copiedId() === snippet.id ? 'Copied' : 'Copy' }}</button>
          <pre [attr.data-testid]="'widget-doc-code-' + snippet.id">{{ snippet.code }}</pre>
        </div>
      </app-modal-option-panel>
    }

  </section>

  <!-- ==================== API key ==================== -->
  <section id="api-key" class="widget-doc__section">
    <h2 class="widget-doc__section-title">2. API key</h2>

    <app-modal-option-panel>
      <div class="widget-doc__fields widget-doc__fields--single">
        @for (param of accessParams; track param.name) {
          <ng-container *ngTemplateOutlet="field; context: { $implicit: param }"></ng-container>
        }
      </div>
      <p class="widget-doc__note">
        The key is checked before any other parameter is read. Without a valid one the widget shows a single
        "Invalid API key" page pointing at support, and none of the app is loaded behind it. The key you type
        here stays in this tab — it is not saved with the rest of the configuration.
      </p>
    </app-modal-option-panel>
  </section>

  <!-- ==================== Presets ==================== -->
  <section id="presets" class="widget-doc__section">
    <h2 class="widget-doc__section-title">3. Presets</h2>

    <div class="widget-doc__presets">
      @for (preset of presets; track preset.id) {
        <button type="button" class="widget-doc__preset" [class.widget-doc__preset--active]="isPresetActive(preset)"
                [attr.data-testid]="'widget-doc-preset-' + preset.id" (click)="applyPreset(preset)">
          <span class="widget-doc__preset-title">{{ preset.title }}</span>
          <span class="widget-doc__preset-desc">{{ preset.description }}</span>
          <span class="widget-doc__preset-action">{{ isPresetActive(preset) ? 'Applied' : 'Apply' }}</span>
        </button>
      }
    </div>
    <p class="widget-doc__note">A preset replaces every parameter except the API key; fine-tune it in the sections below.</p>
  </section>

  <!-- ==================== Rules ==================== -->
  <section id="rules" class="widget-doc__section">
    <h2 class="widget-doc__section-title">4. Parameter rules</h2>

    <app-modal-option-panel>
      <ul class="widget-doc__list">
        <li>Apart from <code>api_key</code> every parameter is <strong>optional</strong>, and parameter names
          are <strong>case-insensitive</strong> — <code>carId</code>, <code>CARID</code> and <code>carid</code>
          are the same.</li>
        <li><strong>Booleans</strong> accept <code>1 / 0</code>, <code>true / false</code>,
          <code>yes / no</code>, <code>on / off</code>. Anything else counts as "not given".</li>
        <li><strong>Numbers are clamped, not rejected</strong>: <code>battery=250</code> becomes
          <code>100</code>, <code>weight=0</code> becomes <code>1</code>. A non-numeric value counts as "not
          given".</li>
        <li><strong>Precedence</strong>: URL parameter → value stored in the WebView's <code>localStorage</code>
          from a previous session → application default. A parameter missing from the URL never overwrites the
          stored value; "not set" on this page means the widget uses that fallback.</li>
        <li>Changes the user makes inside the widget are stored as usual, but reloading the same URL re-applies
          the URL values on top of them.</li>
      </ul>
    </app-modal-option-panel>
  </section>

  <!-- ==================== UI switches ==================== -->
  <section id="ui-switches" class="widget-doc__section">
    <h2 class="widget-doc__section-title">5. UI switches</h2>

    <app-modal-option-panel>
      <div class="widget-doc__fields">
        @for (param of uiSwitchParams; track param.name) {
          <ng-container *ngTemplateOutlet="field; context: { $implicit: param }"></ng-container>
        }
      </div>
      <p class="widget-doc__note">
        With both <code>carselector=0</code> and <code>batteryslider=0</code> the vehicle panel has nothing
        left to show and is hidden entirely.
      </p>
      <p class="widget-doc__note">
        <code>stationlists</code> and <code>operatorlists</code> only affect the charging options of the vehicle
        dialog and the charger popup. With a list hidden, whatever an earlier session stored for it is ignored
        (and kept in storage), so routing only follows lists the user can see — or the ones the URL sets.
      </p>
    </app-modal-option-panel>
  </section>

  <!-- ==================== Vehicle ==================== -->
  <section id="car-params" class="widget-doc__section">
    <h2 class="widget-doc__section-title">6. Vehicle</h2>

    <app-modal-option-panel>
      <div class="widget-doc__fields">
        @for (param of vehicleParams; track param.name) {
          <ng-container *ngTemplateOutlet="field; context: { $implicit: param }"></ng-container>
        }
      </div>
      <p class="widget-doc__note">
        <code>chargers</code> accepts <code>ccs</code>, <code>chademo</code>, <code>schuko</code>,
        <code>tesla</code> (or <code>supercharger</code>), <code>type1</code> and <code>type2</code>, separated by
        comma, semicolon, pipe, plus or space. If none of the names is recognised the parameter is dropped and
        the car's own connector set is used.
      </p>
      <p class="widget-doc__note">
        <code>favoriteoperators</code> takes numeric operator ids with the same separators; pick them above or read
        them from the operator list the widget loads. Unknown ids are passed on and match no station. Combine it
        with <code>operatorlists=0</code> to fix the preferred network without letting the user edit it.
      </p>
    </app-modal-option-panel>
  </section>

  <!-- ==================== Settings ==================== -->
  <section id="settings-params" class="widget-doc__section">
    <h2 class="widget-doc__section-title">7. Application settings</h2>

    <app-modal-option-panel>
      <div class="widget-doc__fields">
        @for (param of settingsParams; track param.name) {
          <ng-container *ngTemplateOutlet="field; context: { $implicit: param }"></ng-container>
        }
      </div>
      <p class="widget-doc__note">
        <code>skin</code> is the one setting that does <strong>not</strong> fall back to the stored value: with
        no <code>skin</code> parameter the widget always renders light, and switching the theme inside the widget
        lasts for that session only. Pass the device's appearance to follow the system dark mode.
      </p>
      <p class="widget-doc__note">
        The per-unit parameters fall back to <code>unit</code>, so <code>unit=imperial&amp;pressure=metric</code>
        yields imperial everywhere except pressure. As soon as any unit parameter is present, the automatic
        "GB/US users get imperial" geo-check is skipped.
      </p>
    </app-modal-option-panel>
  </section>

  <!-- ==================== Colors ==================== -->
  <section id="colors" class="widget-doc__section">
    <h2 class="widget-doc__section-title">8. Colors</h2>

    <app-modal-option-panel>
      <span panel-title>Color builder</span>
      <p class="widget-doc__note widget-doc__note--top">
        Every <code>color_*</code> parameter replaces one color of the widget's palette; the mock below shows the
        result. Unset colors show the value of the current page theme — switch the theme in the bar to start from
        the dark palette.
      </p>
      <!-- Static mock of the widget UI, drawn from the same tokens the widget uses. -->
      <div class="widget-doc__preview" [class.widget-doc__preview--dark]="skin() === 'dark'" [style]="previewStyle()"
           data-testid="widget-doc-color-preview" aria-hidden="true">
        <svg class="widget-doc__preview-map" viewBox="0 0 800 420" preserveAspectRatio="xMidYMid slice">
          <g class="widget-doc__preview-roads">
            <path d="M-20 90 C 150 120, 260 60, 420 110 S 700 160, 820 120" />
            <path d="M-20 300 C 120 280, 300 330, 460 290 S 700 250, 820 310" />
            <path d="M180 -20 C 200 120, 150 250, 220 440" />
            <path d="M560 -20 C 520 110, 600 260, 540 440" />
            <path class="widget-doc__preview-road--minor" d="M-20 200 L 820 180" />
            <path class="widget-doc__preview-road--minor" d="M360 -20 L 400 440" />
            <path class="widget-doc__preview-road--minor" d="M40 -20 C 90 150, 20 300, 90 440" />
            <path class="widget-doc__preview-road--minor" d="M700 -20 C 660 140, 760 280, 720 440" />
          </g>
          <path class="widget-doc__preview-range"
                d="M400 70 C 470 60, 520 110, 580 120 S 660 190, 640 250 S 600 350, 520 350 S 420 390, 350 360 S 240 330, 230 260 S 250 150, 300 120 S 350 78, 400 70 Z" />
          <path class="widget-doc__preview-range widget-doc__preview-range--inner"
                d="M410 140 C 470 135, 530 170, 540 220 S 500 300, 440 300 S 330 300, 320 240 S 350 145, 410 140 Z" />
        </svg>

        <div class="widget-doc__preview-marker">
          <svg viewBox="0 0 24 24"><path d="M12 3 L19 20 L12 16 L5 20 Z" /></svg>
        </div>

        <div class="widget-doc__preview-search">
          <div class="widget-doc__preview-search-field">
            <span>Search on map</span>
            <svg viewBox="0 0 24 24"><circle cx="10.5" cy="10.5" r="6" /><path d="M15 15 L20 20" /></svg>
          </div>
        </div>

        <div class="widget-doc__preview-settings">
          <img [src]="'/assets/icons/' + skin() + '/ui/settings_icon.svg'" alt="" />
        </div>

        <div class="widget-doc__preview-route">
          <div class="widget-doc__preview-title">No route yet</div>
          <div class="widget-doc__preview-subtitle">Add a start point and a destination to plan your route.</div>
          <div class="widget-doc__preview-warning">Destination is out of range</div>
          <div class="widget-doc__preview-buttons">
            <span class="widget-doc__preview-button widget-doc__preview-button--primary">Go</span>
            <span class="widget-doc__preview-button">Cancel</span>
          </div>
        </div>

        <div class="widget-doc__preview-3d">3D</div>

        <div class="widget-doc__preview-vehicle">
          <div class="widget-doc__preview-title">Tesla Model 3</div>
          <div class="widget-doc__preview-subtitle">Standard Range RWD (2019)</div>
          <div class="widget-doc__preview-property">
            <div class="widget-doc__preview-property-row">
              <span>Battery Level</span>
              <span class="widget-doc__preview-stepper">&minus;</span>
              <span class="widget-doc__preview-value">73 %</span>
              <span class="widget-doc__preview-stepper">+</span>
            </div>
            <div class="widget-doc__preview-slider">
              <div class="widget-doc__preview-slider-fill"></div>
              <div class="widget-doc__preview-slider-thumb"></div>
            </div>
          </div>
          <span class="widget-doc__preview-button widget-doc__preview-button--primary">Options</span>
        </div>

        <div class="widget-doc__preview-info">16 °C</div>
      </div>

      <!-- Same cards as the other parameters; the control is a pill filled with the color. -->
      <div class="widget-doc__fields" data-testid="widget-doc-color-builder">
        @for (param of colorParams; track param.name) {
          <div class="widget-doc__field" [class.widget-doc__field--set]="isColorOverridden(param.name)"
               [attr.data-testid]="'widget-doc-field-' + param.name">
            <div class="widget-doc__field-head">
              <code class="widget-doc__param">{{ param.name }}</code>
              <span class="widget-doc__field-spacer"></span>
              @if (isColorOverridden(param.name)) {
                <button type="button" class="widget-doc__field-clear" [attr.aria-label]="'Remove ' + param.name + ' from the URL'"
                        [attr.data-testid]="'widget-doc-color-reset-' + param.name" (click)="clear(param.name)">
                  In URL <span aria-hidden="true">&#x2715;</span>
                </button>
              } @else {
                <span class="widget-doc__field-fallback">not set · {{ skin() }} skin</span>
              }
            </div>
            <p class="widget-doc__field-desc">{{ param.description }}</p>
            <div class="widget-doc__field-control">
              <label class="widget-doc__swatch" [style.--swatch]="colorValues()[param.name]"
                     [class.widget-doc__swatch--dark-text]="isLightColor(colorValues()[param.name])">
                <input type="color" class="widget-doc__swatch-input" [value]="colorValues()[param.name]"
                       [attr.aria-label]="param.name" (input)="setColor(param.name, $event)"
                       [attr.data-testid]="'widget-doc-color-' + param.name" />
                <span class="widget-doc__swatch-value">{{ colorValues()[param.name] }}</span>
                <svg class="widget-doc__swatch-icon" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M4 20h4L19 9l-4-4L4 16v4Z" /><path d="M13.5 6.5l4 4" />
                </svg>
              </label>
            </div>
          </div>
        }
      </div>

      <div class="widget-doc__fields widget-doc__fields--tint">
        <ng-container *ngTemplateOutlet="field; context: { $implicit: maptintParam }"></ng-container>
      </div>

      <div class="widget-doc__actions">
        <button type="button" class="widget-doc__url-button" data-testid="widget-doc-color-reset-all"
                [disabled]="!hasColorChanges()" (click)="resetColors()">Reset colors</button>
      </div>
    </app-modal-option-panel>

    <app-modal-option-panel>
      <span panel-title>What the colors change</span>
      <table class="widget-doc__table">
        <thead>
          <tr>
            <th scope="col">Parameter</th>
            <th scope="col">Token</th>
            <th scope="col">Used for</th>
          </tr>
        </thead>
        <tbody>
          @for (param of colorParams; track param.name) {
            <tr>
              <td><code class="widget-doc__param">{{ param.name }}</code></td>
              <td><code>{{ param.tokens.join(', ') }}</code></td>
              <td>{{ param.description }}</td>
            </tr>
          }
        </tbody>
      </table>
      <p class="widget-doc__note">
        Values are hex colors <strong>without</strong> the <code>#</code> (<code>color_accent1=2FA9D2</code>; 3, 4,
        6 or 8 digits) — a literal <code>#</code> would start the URL fragment. Any other CSS color also works
        (<code>crimson</code>, <code>rgb(47,169,210)</code>); invalid values are ignored. The colors apply on top of
        the <code>skin</code> and stay the same in both skins; gradients and hover states follow the accent colors.
      </p>
      <p class="widget-doc__note">
        <strong>The map follows the palette too.</strong> Every basemap color keeps its lightness and shifts
        <code>maptint</code> % toward the palette's hue — land, parks and buildings toward
        <code>color_background</code>, roads, water and labels toward <code>color_accent1</code>. With
        <code>color_accent1</code> or <code>color_accent2</code> set, the range area, the route line's free-flow
        color, the route planning animation, the turn arrows and the position arrow use the accent color;
        <code>color_lighttext</code> becomes the light fill of the charger pins, the cursor disc and the turn
        arrows. Traffic colors, the charger power-level rings and the pin icons are never recolored.
        <code>maptint=0</code> leaves the map exactly as it is.
      </p>
    </app-modal-option-panel>
  </section>

  <!-- ==================== Map / mode ==================== -->
  <section id="map-params" class="widget-doc__section">
    <h2 class="widget-doc__section-title">9. Map and mode</h2>

    <app-modal-option-panel>
      <div class="widget-doc__fields">
        @for (param of mapParams; track param.name) {
          <ng-container *ngTemplateOutlet="field; context: { $implicit: param }"></ng-container>
        }
      </div>
      @if (isSet('lat') !== isSet('lon')) {
        <p class="widget-doc__note widget-doc__note--warning" data-testid="widget-doc-latlon-warning">
          Only one of <code>lat</code> / <code>lon</code> is set — the widget ignores the start point until both are.
        </p>
      }
      <p class="widget-doc__note">
        <code>lat</code> and <code>lon</code> must both be present to take effect, and <code>zoom</code> only applies
        together with them. With a start point the widget does <strong>not</strong> jump to device positions that
        arrive later — leave both unset if the map should follow the user.
      </p>
    </app-modal-option-panel>
  </section>

  <!-- ==================== Native bridge ==================== -->
  <section id="native-bridge" class="widget-doc__section">
    <h2 class="widget-doc__section-title">10. Native bridge — {{ platformInfo().label }}</h2>

    <app-modal-option-panel>
      <p class="widget-doc__note widget-doc__note--top">
        The widget never asks the WebView for geolocation, so no permission prompt appears inside it: your app
        reads the position with its own location API and pushes it through <code>window.evWidget</code>. The
        API is registered once the widget has started — after the page finished loading and the API key was
        accepted — so the calls below return <code>false</code> until then. Keep the latest fix and retry about
        every 300 ms; the Android sample keeps probing for up to two minutes and restarts on every page load.
      </p>
      @for (snippet of bridgeCode(); track snippet.id) {
        <div class="widget-doc__code">
          <button type="button" class="widget-doc__copy" [attr.data-testid]="'widget-doc-copy-' + snippet.id"
                  (click)="copy(snippet.id, snippet.code)">{{ copiedId() === snippet.id ? 'Copied' : 'Copy' }}</button>
          <pre [attr.data-testid]="'widget-doc-code-' + snippet.id">{{ snippet.code }}</pre>
        </div>
      }
    </app-modal-option-panel>

    <app-modal-option-panel>
      <span panel-title>Position fields</span>
      <table class="widget-doc__table">
        <thead>
          <tr>
            <th scope="col">Field</th>
            <th scope="col">Unit</th>
            <th scope="col">Required</th>
            <th scope="col">Default</th>
          </tr>
        </thead>
        <tbody>
          @for (field of gpsFields; track field.field) {
            <tr>
              <td><code class="widget-doc__param">{{ field.field }}</code></td>
              <td>{{ field.unit }}</td>
              <td>{{ field.required }}</td>
              <td><code>{{ field.fallback }}</code></td>
            </tr>
          }
        </tbody>
      </table>
      <p class="widget-doc__note">
        <code>gpsPositionUpdate</code> also takes positional arguments
        (<code>lat, lng, speed, heading, accuracy, altitude, timestamp</code>) and is aliased as
        <code>window.gpsPositionUpdate</code>. It returns <code>false</code> and does nothing when latitude or
        longitude is missing or out of range. <code>setBattery(level)</code> returns <code>false</code> for a
        non-numeric value; valid values are clamped to 1–100, move the battery slider and are stored like a user
        change. Send about one position per second while the widget is visible.
      </p>
      <p class="widget-doc__note">
        Google Analytics consent: <code>window.evWidget.setAnalyticsConsent(true | false)</code>, or
        <code>postMessage({{ '{' }} type: 'setAnalyticsConsent', granted: true {{ '}' }})</code> into the iframe. Only a real
        boolean is accepted (anything else returns <code>false</code>); <code>false</code> stops Google Analytics and
        deletes its cookies. The choice is not stored, so pass it on every load (or use <code>analytics=1</code>).
      </p>
      <p class="widget-doc__note">
        Every call also works as <code>postMessage({{ '{' }} type: 'gpsPositionUpdate' | 'setBattery' | 'setAnalyticsConsent', … {{ '}' }})</code>
        into the iframe. Messages are accepted only from the page that directly embeds the iframe (its parent
        window) or from the widget's own origin; messages from any other frame are ignored.
      </p>
    </app-modal-option-panel>
  </section>

  </main>
</div>
</div>
